import { CITY } from '@city'
import type { OnlineRoutingOptions, Route } from '@metro/routing'
import type { SchemaData } from '../schema/types'
import { nightWindowState } from './nightWindowState'
import { zonedParts, zoneOffsetMs } from './zonedTime'

/**
 * Night-Tube routing constants + schedule helper, shared across the web
 * Routing pipeline. Byte-for-behaviour mirror of the iOS canonical
 * `NightTube` (`metro-ios/MetroCore/Sources/Routing/NightTube.swift`)
 * and the Android twin — the eligible-line rule, the operating-hours
 * rule, the wire `depart_at` format, and the safety-net filter are the
 * cross-platform contract.
 *
 * ## Night-eligible lines
 * The lines the map draws in night mode ({@link nightEligibleLineIds}):
 * Piccadilly, Victoria, Central, Jubilee, Northern, and the Windrush
 * Overground sub-line on both editions.
 *
 * ## Operating hours
 * No operating-hours data exists in the schema / server (only the
 * per-element `night` MODE flag), so each city carries its published night
 * schedule as `CityConfig.nightWindow`, in its own zone: London's Night Tube
 * runs **Friday and Saturday nights 00:30–05:00 Europe/London** (the small
 * hours of Saturday and Sunday), New York's Late Night **every night
 * 00:00–06:30 America/New_York** (TASK-1136). {@link nextNightTubeDeparture}
 * reads it through `nightWindowState`; iOS/Android mirror the same rule.
 */

/**
 * Line ids that run a night service: every line with at least one
 * connection drawn in night mode. Read from the map data, so v1
 * (`line-pic`, …) and v2 (`piccadilly`, …) editions agree without an id
 * list in code (spec 2026-10-13 §4). Both London editions yield the same
 * six lines: Piccadilly, Victoria, Central, Jubilee, Northern and the
 * Windrush Overground.
 */
export function nightEligibleLineIds(schema: SchemaData): ReadonlySet<string> {
  const lines = new Set<string>()
  for (const c of schema.connections) {
    if (c.modes?.includes('night') === true) lines.add(c.lineId)
  }
  return lines
}

/**
 * The `departAt` an online night-mode routing request should carry so the
 * backend / TfL Journey Planner returns real **night** services rather than
 * the current-day timetable.
 *
 * - When `now` already sits inside the city's night window
 *   (`CITY.nightWindow`, e.g. London Sat/Sun 00:30–05:00) → returns `now`
 *   (depart now).
 * - Otherwise → the START of the next window. A city without a night
 *   service departs now.
 *
 * Pure + deterministic for a given `now`. Mirrors iOS
 * `NightTube.nextNightTubeDeparture(now:)`.
 */
export function nextNightTubeDeparture(now: Date): Date {
  const window = CITY.nightWindow
  if (window === null) return now
  const state = nightWindowState(now, window, CITY.timeZone)
  return state.active ? now : state.nextBoundary
}

/**
 * Formats a `departAt` `Date` into the wire string the backend
 * `POST /api/v1/routes` expects. The server validates **RFC3339**, which
 * REQUIRES a timezone offset — a bare `yyyy-MM-dd'T'HH:mm:ss` (no offset) is
 * rejected with HTTP 400 `invalid depart_at: must be RFC3339` (verified against
 * the live endpoint 2026-07-09). So emit the city's wall clock (`CITY.timeZone`)
 * WITH its DST-correct offset (London `+01:00` BST / `+00:00` GMT, New York
 * `-04:00` / `-05:00`), e.g. `2026-07-11T00:30:00+01:00`. Mirrors iOS/Android `NightTube.departAtString`.
 */
export function departAtString(date: Date): string {
  const p = zonedParts(date, CITY.timeZone)
  const two = (n: number): string => String(n).padStart(2, '0')
  // DST-correct offset of the city's zone at this instant, `+HH:MM` / `-HH:MM`.
  const offMin = Math.round(zoneOffsetMs(date.getTime(), CITY.timeZone) / 60000)
  const sign = offMin < 0 ? '-' : '+'
  const absMin = Math.abs(offMin)
  return `${p.year}-${two(p.month)}-${two(p.day)}T${two(p.hour)}:${two(p.minute)}:${two(p.second)}${sign}${two(Math.floor(absMin / 60))}:${two(absMin % 60)}`
}

/**
 * When a route found now should leave: in night mode outside a Night-Tube
 * window, the start of the next one (`nextNightTubeDeparture`) — the instant
 * the request plans for and the route's times count from; otherwise
 * `undefined` (it leaves now). Mirrors iOS / Android `NightTube.plannedDeparture`.
 */
export function plannedNightDeparture(isNightMode: boolean, now: Date): Date | undefined {
  if (!isNightMode) return undefined
  const departure = nextNightTubeDeparture(now)
  return departure.getTime() > now.getTime() ? departure : undefined
}

/**
 * The `OnlineRoutingOptions` a night-mode routing request should carry, or
 * `undefined` in normal mode. In night mode the online request gets a
 * `depart_at` at the next Night-Tube window so the backend returns night
 * services, not the day timetable.
 */
export function buildNightRoutingOptions(
  isNightMode: boolean,
  now: Date,
): OnlineRoutingOptions | undefined {
  if (!isNightMode) return undefined
  return { departAt: departAtString(nextNightTubeDeparture(now)) }
}

/**
 * Safety net (cross-platform): drops any route that rides a line NOT in
 * `nightLineIds` (see {@link nightEligibleLineIds}). A night route must be composed exclusively of
 * night-service lines; a stray day line (or a phantom / unresolved line
 * code returned by an upstream) is filtered out rather than surfaced. Only
 * `ride` legs carry a `line_id` — transfer / walk legs are ignored. No-op
 * (returns the same reference) when `isNightMode` is false.
 */
export function filterNightIneligibleRoutes(
  routes: readonly Route[],
  isNightMode: boolean,
  nightLineIds: ReadonlySet<string>,
): readonly Route[] {
  if (!isNightMode) return routes
  return routes.filter((route) =>
    route.legs.every((leg) => {
      if (leg.type !== 'ride') return true
      return nightLineIds.has(leg.line_id ?? '')
    }),
  )
}
